import { useState, useCallback, useEffect } from 'react';
import { useAuth, useAuthActions } from '../state/AuthContext';
import { useDispatch } from '../state/AppContext';
import { VIEW_COLLISION, VIEW_COLLISION_PREVIEW } from '../state/actions';
import { getAvatarUrl, favoriteFriend, unfavoriteFriend } from '../api/authClient';
import { getMatchTier } from '../utils/compatibility';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import AddFriendModal from './AddFriendModal';
import InviteLinkModal from './InviteLinkModal';
import '../styles/friends.css';
import '../styles/universe-preview.css';

function FriendsSection() {
  const { user, friends, friendRequests, favorites, discoveredArtists } = useAuth();
  const { acceptFriend, rejectFriend, removeFriend, showAuthModal, refreshFriends } = useAuthActions();
  const appDispatch = useDispatch();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [activeTab, setActiveTab] = useState('friends'); // 'friends' | 'requests'
  const [selectedCard, setSelectedCard] = useState(null);

  // Pull-to-refresh: attach to the LandingPage scroll container so the
  // gesture works anywhere inside the friends tab.
  const [scrollEl, setScrollEl] = useState(null);
  useEffect(() => {
    setScrollEl(document.querySelector('.landing-content'));
  }, []);
  const { pullDistance, isRefreshing, threshold } = usePullToRefresh(
    scrollEl,
    refreshFriends,
    !!user,
  );
  const indicatorProgress = Math.min(pullDistance / threshold, 1);

  const toggleFavFriend = useCallback(async (friendId, isFav, e) => {
    e.stopPropagation();
    try {
      if (isFav) {
        await unfavoriteFriend(friendId);
      } else {
        await favoriteFriend(friendId);
      }
      refreshFriends();
    } catch {
      // Silently fail
    }
  }, [refreshFriends]);

  const artistCount = (favorites?.length || 0) + (discoveredArtists?.length || 0);
  const showFriendsHelper = !user || artistCount < 3;

  const handleRemoveFriend = () => {
    if (confirmRemove) {
      removeFriend(confirmRemove.id);
      setConfirmRemove(null);
    }
  };

  return (
    <div className="friends-section">
      {user && (pullDistance > 0 || isRefreshing) && (
        <div
          className={`friends-ptr-indicator${isRefreshing ? ' refreshing' : ''}`}
          style={{
            opacity: isRefreshing ? 1 : indicatorProgress,
            transform: `scale(${0.6 + indicatorProgress * 0.4})`,
          }}
        >
          <svg
            className="friends-ptr-spinner"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            width="18"
            height="18"
            style={{
              transform: isRefreshing ? undefined : `rotate(${indicatorProgress * 360}deg)`,
            }}
          >
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        </div>
      )}
      {user && (
        <>
        <h3 className="tab-section-header" style={{ marginBottom: '8px' }}>
          Friends
          {friends.length > 0 && <span className="section-count">{friends.length}</span>}
        </h3>
        <div className="friends-action-bar">
          {friendRequests.length > 0 && (
            <button
              className="friends-requests-badge"
              onClick={() => setActiveTab('requests')}
              title={`${friendRequests.length} pending request${friendRequests.length !== 1 ? 's' : ''}`}
            >
              {friendRequests.length}
            </button>
          )}
          <button
            className="friends-invite-btn"
            onClick={() => setShowInviteModal(true)}
            title="Invite a friend"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
            Invite
          </button>
          <button
            className="friends-add-btn"
            onClick={() => setShowAddModal(true)}
            title="Add friend"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span className="add-btn-label">Add Friend</span>
          </button>
        </div>
        </>
      )}
      {showFriendsHelper && (
        <p className="friends-helper-text">
          <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
            <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
          </svg>
          Favorite or discover at least three artists to collide with friends
        </p>
      )}

      {/* Signed out */}
      {!user && (
        <div className="friends-scroll">
          <button
            className="section-placeholder-card"
            onClick={() => showAuthModal('register')}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            <span className="section-placeholder-text">Sign in to add friends</span>
          </button>
        </div>
      )}

      {/* Collide Preview — shown when signed out or no friends */}
      {(!user || (user && friends.length === 0)) && (
        <>
          <p className="friends-helper-text" style={{ marginTop: '16px' }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
              <circle cx="9" cy="12" r="6" />
              <circle cx="15" cy="12" r="6" />
            </svg>
            See how colliding universes works
          </p>
          <div className="universe-preview-btn-wrap">
            <button className="universe-preview-btn" onClick={() => appDispatch({ type: VIEW_COLLISION_PREVIEW })}>
              <span className="universe-preview-btn-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="20" height="20">
                  <circle cx="9" cy="12" r="6" />
                  <circle cx="15" cy="12" r="6" />
                </svg>
              </span>
              <span className="universe-preview-btn-label">Preview Collision</span>
            </button>
          </div>
        </>
      )}

      {/* Signed in — tab bar for friends vs requests */}
      {user && (friendRequests.length > 0 || friends.length > 0) && (
        <>
          {friendRequests.length > 0 && (
            <div className="friends-tabs">
              <button
                className={`friends-tab ${activeTab === 'friends' ? 'active' : ''}`}
                onClick={() => setActiveTab('friends')}
              >
                Friends{friends.length > 0 ? ` (${friends.length})` : ''}
              </button>
              <button
                className={`friends-tab ${activeTab === 'requests' ? 'active' : ''}`}
                onClick={() => setActiveTab('requests')}
              >
                Requests ({friendRequests.length})
              </button>
            </div>
          )}

          {/* Friend requests */}
          {activeTab === 'requests' && friendRequests.length > 0 && (
            <div className="friends-list">
              {friendRequests.map((req) => (
                <div key={req.id} className="friend-request-card">
                  <div className="friend-card-avatar">
                    {req.hasAvatar ? (
                      <img src={getAvatarUrl(req.id)} alt="" className="friend-card-avatar-img" />
                    ) : (
                      req.displayName?.charAt(0) || '?'
                    )}
                  </div>
                  <div className="friend-card-info">
                    <span className="friend-card-name">{req.displayName}</span>
                    <span className="friend-card-username">@{req.username}</span>
                  </div>
                  <div className="friend-request-actions">
                    <button
                      className="friend-accept-btn"
                      onClick={() => { acceptFriend(req.id); setActiveTab('friends'); }}
                      title="Accept"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </button>
                    <button
                      className="friend-reject-btn"
                      onClick={() => rejectFriend(req.id)}
                      title="Decline"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Friends list */}
          {activeTab === 'friends' && friends.length > 0 && (
            <div className="friends-scroll">
              {friends.map((friend) => {
                const isFav = !!friend.isFavorite;
                return (
                <div
                  key={friend.id}
                  className="friend-card"
                  onClick={() => setSelectedCard(selectedCard === friend.id ? null : friend.id)}
                >
                  <button
                    className={`friend-card-remove${selectedCard === friend.id ? ' visible' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setConfirmRemove(friend); }}
                    title="Remove friend"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                  <div className="friend-card-starfield" aria-hidden="true">
                    <span /><span /><span /><span /><span />
                    <span /><span /><span /><span /><span />
                    <span /><span /><span /><span /><span />
                  </div>
                  <div className="friend-card-main">
                    <div className="friend-card-avatar-wrap">
                      <div className="friend-card-avatar">
                        {friend.hasAvatar ? (
                          <img src={getAvatarUrl(friend.id)} alt="" className="friend-card-avatar-img" />
                        ) : (
                          friend.displayName?.charAt(0) || '?'
                        )}
                      </div>
                      <button
                        className={`friend-card-fav-avatar${isFav ? ' active' : ''}`}
                        onClick={(e) => toggleFavFriend(friend.id, isFav, e)}
                        title={isFav ? 'Remove from favorites' : 'Favorite friend'}
                      >
                        <svg viewBox="0 0 24 24" fill={isFav ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" width="10" height="10">
                          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                        </svg>
                      </button>
                    </div>
                    <div className="friend-card-info-col">
                      <span className="friend-card-name-small">{friend.displayName}</span>
                      {friend.username && (
                        <span className="friend-card-username-small">@{friend.username}</span>
                      )}
                      <span className="friend-card-artist-count">
                        {friend.artistCount || 0} artist{friend.artistCount === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="friend-card-collide-col">
                      <button
                        className="friend-card-collide-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          appDispatch({ type: VIEW_COLLISION, friendId: friend.id });
                        }}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                          <circle cx="8" cy="12" r="5" />
                          <circle cx="16" cy="12" r="5" />
                        </svg>
                        Collide
                      </button>
                      {typeof friend.matchPercent === 'number' ? (
                        <>
                          <span className="friend-card-compat">
                            {friend.matchPercent}% match
                          </span>
                          {(() => {
                            const tier = getMatchTier(friend.coreOverlapCount);
                            return tier ? (
                              <span
                                className={`friend-card-tier friend-card-tier-${tier.split(' ')[0].toLowerCase()}`}
                              >
                                {tier}
                              </span>
                            ) : null;
                          })()}
                        </>
                      ) : (
                        <span className="friend-card-compat friend-card-compat-empty">
                          Collide to see match
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )}

          {/* Active tab is friends but none yet */}
          {activeTab === 'friends' && friends.length === 0 && (
            <div className="friends-scroll">
              <div className="section-placeholder-card empty">
                <span className="section-placeholder-text">No friends yet</span>
              </div>
            </div>
          )}
        </>
      )}

      {/* No friends and no requests */}
      {user && friends.length === 0 && friendRequests.length === 0 && (
        <div className="friends-scroll">
          <div className="section-placeholder-card empty">
            <span className="section-placeholder-text">Add friends by username</span>
          </div>
        </div>
      )}

      {showAddModal && <AddFriendModal onClose={() => setShowAddModal(false)} />}
      {showInviteModal && <InviteLinkModal onClose={() => setShowInviteModal(false)} />}

      {confirmRemove && (
        <div className="auth-modal-overlay" onClick={(e) => e.target === e.currentTarget && setConfirmRemove(null)}>
          <div className="confirm-remove-modal">
            <p className="confirm-remove-text">
              Remove <strong>{confirmRemove.displayName}</strong> as a friend?
            </p>
            <div className="confirm-remove-actions">
              <button className="confirm-remove-cancel" onClick={() => setConfirmRemove(null)}>
                Cancel
              </button>
              <button className="confirm-remove-confirm" onClick={handleRemoveFriend}>
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FriendsSection;
